import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

export const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to convert PDF to base64'));
        return;
      }

      const [, base64 = ''] = result.split(',');
      if (!base64) {
        reject(new Error('Failed to extract base64 PDF content'));
        return;
      }

      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read PDF blob'));
    reader.readAsDataURL(blob);
  });

export const getPrimaryCamperName = (registration: any): string =>
  registration?.registrationInfo?.primaryCamperName ||
  `${registration?.firstName || ''} ${registration?.lastName || ''}`.trim() ||
  'Camper';

const BERWICK_CAMP_SITE_URL = 'https://berwickregistration.web.app';

export const buildInvoiceEmailBody = (primaryCamperName: string) => ({
  html: `<p>Hello ${primaryCamperName},</p><p>Please find attached your invoice for camp this year.</p><p>On the invoice you will find the accommodations reserved in your name, as well as the current outstanding balance.</p><p>You can also log in at <a href="${BERWICK_CAMP_SITE_URL}">berwickregistration.web.app</a> any time to view your invoice or make a payment.</p><p>If you have any questions, please feel free to reach out to registraruccma@gmail.com at any time and I will get back to you as soon as I am able.</p>`,
  text: `Hello ${primaryCamperName},\n\nPlease find attached your invoice for camp this year.\n\nOn the invoice you will find the accommodations reserved in your name, as well as the current outstanding balance.\n\nYou can also log in at ${BERWICK_CAMP_SITE_URL} any time to view your invoice or make a payment.\n\nIf you have any questions, please feel free to reach out to registraruccma@gmail.com at any time and I will get back to you as soon as I am able.`,
});

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

export const buildCustomInvoiceEmailBody = (primaryCamperName: string, bodyText: string) => {
  const trimmed = bodyText.trim();
  const paragraphsHtml = trimmed
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');

  return {
    html: `<p>Hello ${primaryCamperName},</p>${paragraphsHtml}`,
    text: `Hello ${primaryCamperName},\n\n${trimmed}`,
  };
};

export const queueInvoiceEmail = async ({
  recipientEmail,
  primaryCamperName,
  invoiceNumber,
  pdfBase64,
  subject,
  body,
}: {
  recipientEmail: string;
  primaryCamperName: string;
  invoiceNumber: string;
  pdfBase64: string;
  subject?: string;
  body?: { html: string; text: string };
}): Promise<void> => {
  const { html, text } = body || buildInvoiceEmailBody(primaryCamperName);

  await addDoc(collection(db, 'emails'), {
    to: [{ email: recipientEmail.toLowerCase() }],
    from: {
      email: 'registraruccma@gmail.com',
      name: 'Berwick Camp',
    },
    subject: subject || 'Your Berwick Camp Invoice',
    html,
    text,
    attachments: [
      {
        filename: `${invoiceNumber}.pdf`,
        content: pdfBase64,
        contentType: 'application/pdf',
      },
    ],
    status: 'pending',
    createdAt: serverTimestamp(),
  });
};
